import { i } from './lit-element-DJ-EMK-H.js';
import wishlistManager from './wishlist-manager.js';
import { isGuest } from './wishlist-utils.js';
import './wishlist-api.js';
import './wishlist-modal-component.js';
import './wishlist-icons.js';
import './wishlist-new-list-component.js';
import './wishlist-product-card-mini.js';
import './wishlist-delete-confirmation-modal.js';
import './wishlist-image-grid.js';
import './wishlist-login-prompt-component.js';

/**
 * Wishlist Heart Component
 * Displays an interactive heart icon that shows whether a product is in any wishlist
 * and provides access to wishlist management
 */
class WishlistHeartComponent extends i {
  static properties = {
    productId: { type: String, reflect: true, attribute: 'product-id' },
    variantId: { type: String, reflect: true, attribute: 'variant-id' },
    productTitle: { type: String, reflect: true, attribute: 'product-title' },
    productHandle: { type: String, reflect: true, attribute: 'product-handle' },
    productPrice: { type: Number, reflect: true, attribute: 'product-price' },
    productCompareAtPrice: { type: Number, reflect: true, attribute: 'product-compare-at-price' },
    productImageUrl: { type: String, reflect: true, attribute: 'product-image-url' },
    productOnStock: { type: Boolean, reflect: true, attribute: 'product-on-stock' },
    sourceUrl: { type: String, reflect: true, attribute: 'source-url' },
    fromCart: { type: Boolean, reflect: true, attribute: 'from-cart' },
    listCount: { type: Number, state: true },
    dataFetched: { type: Boolean, state: true },
  };

  constructor() {
    super();
    this.listCount = 0;
    this.dataFetched = false;
    this._handleClick = this._handleClick.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this._attachEventListeners();
    this._updateHeartVisibility();

    // Check if we need to refresh our state
    if (this.variantId && !this.dataFetched && wishlistManager.initialLoadComplete) {
      this.refreshState();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._detachEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === 'variant-id' && newValue && newValue !== oldValue && this.isConnected) {
      this.dataFetched = false;
      this.listCount = 0;
      this._updateHeartVisibility();
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('listCount')) {
      this._updateHeartVisibility();
    }
  }

  /**
   * Handles click event on the heart button
   * @param {Event} e - Click event
   * @private
   */
  _handleClick(e) {
    e.preventDefault();
    if (!this.variantId) return;

    // Pass product data to wishlistManager
    // For guests, wishlistManager will handle adding to default list
    wishlistManager.openModal(this._getProductData());
  }

  /**
   * Creates product data object from component properties
   * @returns {Object} - Product data object
   * @private
   */
  _getProductData() {
    const data = {
      product_id: this.productId,
      variant_id: this.variantId,
      title: this.productTitle,
      handle: this.productHandle,
      price: this.productPrice,
      compare_at_price: this.productCompareAtPrice,
      image_url: this.productImageUrl,
      on_stock: this.productOnStock,
      source_url: this.sourceUrl,
    };
    if (this.fromCart) {
      data.fromCart = true;
    }
    return data;
  }

  /**
   * Attaches event listeners to the heart button
   * @private
   */
  _attachEventListeners() {
    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', this._handleClick);
  }

  /**
   * Detaches event listeners from the heart button
   * @private
   */
  _detachEventListeners() {
    const button = this.querySelector('button');
    if (!button) return;

    button.removeEventListener('click', this._handleClick);
  }

  /**
   * Updates the visibility of heart icons and count badge
   * @private
   */
  _updateHeartVisibility() {
    const emptyHeart = this.querySelector('.wishlist-heart-empty');
    const filledHeart = this.querySelector('.wishlist-heart-filled');

    if (!emptyHeart || !filledHeart) return;

    const isInWishlist = this.listCount > 0;
    emptyHeart.classList.toggle('hidden', isInWishlist);
    filledHeart.classList.toggle('hidden', !isInWishlist);

    this._updateCountBadge(isInWishlist);
  }

  /**
   * Updates the count badge display
   * @param {boolean} isVisible - Whether the badge should be visible
   * @private
   */
  _updateCountBadge(isVisible) {
    const badge = this.querySelector('.wishlist-list-count-badge');
    if (!badge) return;

    // For guests, we don't show the count badge
    if (isGuest()) {
      badge.classList.add('hidden');
      return;
    }

    if (isVisible) {
      badge.textContent = this.listCount;
      badge.classList.remove('hidden');
    } else {
      badge.classList.add('hidden');
    }
  }

  /**
   * Refreshes the heart state from wishlist manager
   * Call this when the component needs to update its state
   */
  refreshState() {
    if (!this.variantId) return;

    const variantId = String(this.variantId);
    const listCount = wishlistManager.productStates.get(variantId) || 0;

    this.listCount = listCount;
    this.dataFetched = true;

    // Ensure this variant is tracked in the product states
    if (!wishlistManager.productStates.has(variantId)) {
      wishlistManager.productStates.set(variantId, 0);
    }
  }

  createRenderRoot() {
    return this;
  }
}

if (!customElements.get('wishlist-heart-component')) {
  customElements.define('wishlist-heart-component', WishlistHeartComponent);
}

export { WishlistHeartComponent };
